/**
 * Kind + provider-type vocabulary rendering for the Saoirse Console.
 *
 * The kind LIST is always derived from loaded rows (design 2026-06-10 §2:
 * "kinds … client-side from loaded rows", no hardcoded vocabulary drives what
 * renders). This module only supplies stable colors for kinds we know about,
 * so the console stays visually consistent with the retired
 * substrate-editor.html pages. A kind present in data but missing from the
 * palette is NOT silently grayed out (the old pages' failure mode): it gets a
 * deliberately loud fallback color and a `known: false` flag the UI must
 * surface.
 *
 * KNOWN_KIND_COLORS covers the 13 values of the saoirse_v2.landmarks.kind
 * CHECK constraint as of 2026-06-10 — the 12 in live data plus `art_gallery`
 * (added 2026-05-25, zero rows yet). Palette inherited from
 * substrate-editor.html; art_gallery color is new.
 */

export const KNOWN_KIND_COLORS: Record<string, string> = {
  landmark: '#92400e',
  church: '#7c3aed',
  museum: '#0369a1',
  art_gallery: '#be185d',
  square: '#6b7280',
  park: '#15803d',
  viewpoint: '#059669',
  bridge: '#1e3a8a',
  cafe: '#ea580c',
  restaurant: '#dc2626',
  bar: '#db2777',
  shop: '#ca8a04',
  gelateria: '#f59e0b',
};

/** Canonical display order for known kinds (legend + lists). */
export const KIND_ORDER: string[] = Object.keys(KNOWN_KIND_COLORS);

/** Loud, impossible-to-miss color for kinds the palette does not know. */
export const UNKNOWN_KIND_COLOR = '#ff00e5';

export type KindInfo = {
  kind: string;
  count: number;
  color: string;
  /** false → not in KNOWN_KIND_COLORS; the UI must mark it visibly. */
  known: boolean;
};

export function kindColor(kind: string): string {
  return KNOWN_KIND_COLORS[kind] ?? UNKNOWN_KIND_COLOR;
}

/**
 * Normalize a raw kind for grouping/display: null/empty → '(none)'.
 * Map features, legend chips and list rows must all use this same key, or
 * a null-kind row would be filterable in one place and not another.
 */
export function normalizeKind(kind: string | null | undefined): string {
  return kind && kind.trim() !== '' ? kind : '(none)';
}

/**
 * Distinct kinds present in `rows` with counts: known kinds first in
 * canonical order, unknown kinds appended alphabetically (never dropped).
 */
export function deriveKinds(rows: ReadonlyArray<{ kind: string | null }>): KindInfo[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const kind = normalizeKind(row.kind);
    counts.set(kind, (counts.get(kind) ?? 0) + 1);
  }
  const known: KindInfo[] = [];
  for (const kind of KIND_ORDER) {
    const count = counts.get(kind);
    if (count !== undefined) {
      known.push({ kind, count, color: kindColor(kind), known: true });
    }
  }
  const unknown: KindInfo[] = [...counts.keys()]
    .filter((kind) => !(kind in KNOWN_KIND_COLORS))
    .sort((a, b) => a.localeCompare(b))
    .map((kind) => ({
      kind,
      count: counts.get(kind) ?? 0,
      color: UNKNOWN_KIND_COLOR,
      known: false,
    }));
  return [...known, ...unknown];
}

/**
 * Provider business_type colors — same approach as kinds: palette from the
 * retired editor pages, loud fallback for unrecognized types.
 */
export const KNOWN_PROVIDER_TYPE_COLORS: Record<string, string> = {
  restaurant: '#dc2626',
  bar: '#db2777',
  food_drink: '#f97316',
  venue: '#6366f1',
  activity: '#10b981',
};

export const UNKNOWN_PROVIDER_TYPE_COLOR = '#ff00e5';

export function providerTypeColor(businessType: string | null | undefined): string {
  if (!businessType) return UNKNOWN_PROVIDER_TYPE_COLOR;
  return KNOWN_PROVIDER_TYPE_COLORS[businessType] ?? UNKNOWN_PROVIDER_TYPE_COLOR;
}
